import { collisions } from './data/collisions_SignmatesTown';
import { CollisionBlock, Sprite } from './classes';
import { 
  GAME_STATES, NPC_IDS, NPC_DIALOGUES, DREAM_DIALOGUES, 
  DREAM_POSTER, DREAM_REFLECTION, getDefaultDialogue, 
  isNPCInteractable, getNextGameState 
} from './gameState';

import backgroundImgSrc from './img/SignMatesTown.png';
import foregroundImgSrc from './img/foregroundObjects_Signmates.png';
import startImageSrc from './img/StartImage.png';  // Add start image import
import playerDownSrc from './img/playerDown.png';
import playerUpSrc from './img/playerUp.png';
import playerLeftSrc from './img/playerLeft.png';
import playerRightSrc from './img/playerRight.png';
import teacher1Src from './img/Sprites/teacher1.png';
import teacher2Src from './img/Sprites/teacher2.png';
import children1Src from './img/Sprites/children1.png';
import children2Src from './img/Sprites/children2.png';
import children3Src from './img/Sprites/children3.png';
import fatherSrc from './img/Sprites/father.png';
import motherSrc from './img/Sprites/mother.png';
import doctorSrc from './img/Sprites/Doctor.png';
import nurseSrc from './img/Sprites/nurse.png';
import oldManSrc from './img/Sprites/old man.png';

// Add font import
const retroFont = new FontFace('Press Start 2P', 'url(https://fonts.googleapis.com/css2?family=Press+Start+2P&display=swap)');
retroFont.load().then(font => {
  document.fonts.add(font);
}).catch(err => console.error('Error loading font:', err));

// API URL
const API_URL = process.env.REACT_APP_URL || 'https://signmates-be-new-bmbsccc8dwhrgzgf.australiaeast-01.azurewebsites.net';

// Variables to store API results
let auslanDhhChildrenData = null;
let emotionEmpathyComparisonData = null;

// Move these to module scope so all functions can access them
let backgroundMusic = null;
let isMusicPlaying = false;
let collisionSound = null;
let dialoguePopSound = null;
let dreamMusic = null;  // Add dream music variable
let gameCompleteMusic = null;
let itemAcquisitionSound = null;  // Add this line

export function stopBackgroundMusic() {
  if (backgroundMusic && isMusicPlaying) {
    backgroundMusic.pause();
    isMusicPlaying = false;
  }
}

export function startGame(canvas) {
  const ctx = canvas.getContext('2d');
  canvas.width = 1024;
  canvas.height = 576;

  // Add start screen and introduction states
  let showStartScreen = true;
  let showIntroduction = false;
  let startScreenBlinkTimer = 0;
  let showBlinkText = true;
  const startImage = new Image();
  startImage.src = startImageSrc;

  // Introduction text
  const introductionText = [
    "🎒 A New Day Begins...",
    "",
    "You are Jayden.",
    "Today at school, you'll learn about Deaf and Hard of Hearing (DHH) people by talking to your teachers and friends.",
    "Discover what empathy and inclusion really mean.",
    "",
    "Later, in a dream, you'll experience life from a DHH perspective.",
    "",
    "Hint: There's a hidden easter egg in the game. Find it to unlock a special achievement! 🏆",
    "",
    "Let's get started!"
  ];

  // Game state
  let currentGameState = GAME_STATES.INITIAL;
  let currentDialogue = null;
  let currentDialogueIndex = 0;
  let isInDialogue = false;
  let currentNPC = null;
  let playerCanMove = true;
  let hasKnowledgeBadge = false; // Add state for knowledge badge

  const collisionMap = [];

// 100 is the width of the collision map, change it to the width of the collision map

  for (let i = 0; i < collisions.length; i += 100) {
    collisionMap.push(collisions.slice(i, i + 100));
  }

  const offset = { x: -460, y: -1620 };

  // Create NPC sprites with interaction zones
  const npcSprites = [];
  const npcInteractionZones = new Map(); // Store interaction zones for each NPC
  const npcImagePaths = {
    2978: teacher1Src,
    3041: children1Src,
    2562: teacher2Src,
    2469: fatherSrc,
    2052: motherSrc,
    2652: children3Src,
    3068: children2Src,
    1780: doctorSrc,
    2092: nurseSrc,
    1767: oldManSrc
  };

  // Load NPC images
  const npcImageObjects = {};
  Object.entries(npcImagePaths).forEach(([id, path]) => {
    const img = new Image();
    img.src = process.env.PUBLIC_URL + path;
    npcImageObjects[id] = img;
  });

  // Create player first so we can reference it in collision detection
  const playerDownImage = new Image();
  playerDownImage.src = playerDownSrc;
  const playerUpImage = new Image();
  playerUpImage.src = playerUpSrc;
  const playerLeftImage = new Image();
  playerLeftImage.src = playerLeftSrc;
  const playerRightImage = new Image();
  playerRightImage.src = playerRightSrc;

  const player = new Sprite({
    position: {
      x: canvas.width / 2 - 192 / 4 / 2,
      y: canvas.height / 2 - 68 / 2
    },
    image: playerDownImage,
    frames: { max: 4 },
    sprites: {
      up: playerUpImage,
      down: playerDownImage,
      left: playerLeftImage,
      right: playerRightImage
    }
  });

  // Initialize player dimensions
  player.width = 48;  // Set a default width
  player.height = 68; // Set a default height

  // Store original NPC positions for interaction zones
  const npcOriginalPositions = new Map();

  // Create NPC sprites at collision locations
  collisionMap.forEach((row, i) => {
    row.forEach((symbol, j) => {
      if (npcImagePaths[symbol]) {
        const position = {
          x: j * CollisionBlock.width + offset.x -20,
          y: i * CollisionBlock.height + offset.y -20
        };
        
        // Create NPC sprite with its ID
        const npc = new Sprite({
          position,
          image: npcImageObjects[symbol],
          frames: { max: 1 },
          scale: 1.3  // Set NPC scale to 1.5x
        });
        npc.npcId = symbol; // Add NPC ID to the sprite object
        npcSprites.push(npc);
        
        // Store original position for this NPC
        npcOriginalPositions.set(symbol, { ...position });
        
        // Create interaction zone
        npcInteractionZones.set(symbol, {
          position: {
            x: position.x - 10,
            y: position.y 
          },
          width: CollisionBlock.width + 40,
          height: CollisionBlock.height + 40,
          npcId: symbol,
          originalX: position.x - 20,
          originalY: position.y - 20
        });
      }
    });
  });

  const boundaries = [];
  collisionMap.forEach((row, i) => {
    row.forEach((symbol, j) => {
      if (symbol === 1565) {
        boundaries.push(
          new CollisionBlock({
            position: {
              x: j * CollisionBlock.width + offset.x,
              y: i * CollisionBlock.height + offset.y
            }
          })
        );
      }
    });
  });

  const backgroundImage = new Image();
  backgroundImage.src = backgroundImgSrc;
  const foregroundImage = new Image();
  foregroundImage.src = foregroundImgSrc;

  const background = new Sprite({ position: { x: offset.x, y: offset.y }, image: backgroundImage });
  const foreground = new Sprite({ position: { x: offset.x, y: offset.y }, image: foregroundImage });

  const keys = {
    w: { pressed: false },
    s: { pressed: false },
    a: { pressed: false },
    d: { pressed: false },
    ArrowUp: { pressed: false },
    ArrowDown: { pressed: false },
    ArrowLeft: { pressed: false },
    ArrowRight: { pressed: false },
  };

  // Add NPC sprites to movables array with their interaction zones
  const movables = [
    background,
    ...boundaries,
    ...npcSprites.map(npc => ({
      ...npc,
      update: function() {
        // Update the corresponding interaction zone when this NPC moves
        const zone = npcInteractionZones.get(this.npcId);
        if (zone) {
          zone.position.x = this.position.x - 13;
          zone.position.y = this.position.y - 0.3;
        }
      }
    })),
    foreground
  ];

  function rectangleCollision({ rectangle1, rectangle2 }) {
    return (
      rectangle1.position.x + rectangle1.width >= rectangle2.position.x &&
      rectangle1.position.x <= rectangle2.position.x + rectangle2.width &&
      rectangle1.position.y + rectangle1.height >= rectangle2.position.y &&
      rectangle1.position.y <= rectangle2.position.y + rectangle2.height
    );
  }

  let lastKey = '';

  // Function to check if player is near an NPC
  function isPlayerNearNPC() {
    if (!player || !player.position) {
      return null;
    }
    
    for (const [npcId, zone] of npcInteractionZones) {
      const collision = rectangleCollision({
        rectangle1: {
          position: player.position,
          width: player.width,
          height: player.height
        },
        rectangle2: zone
      });

      if (collision) {
        return npcId;
      }
    }
    return null;
  }

  // Add dream state variables
  let isTransitioningToDream = false;
  let dreamTransitionProgress = 0;
  let showDreamNarration = false;
  let dreamNarrationOpacity = 0;
  let dreamNarrationIndex = 0;
  const dreamNarrationText = [
    "You have entered a dream...",
    "When you wake up, you are not able to hear any sounds.",
    "Speak with your parents."
  ];
  let showPoster = false;
  let posterOpacity = 0;
  let showReflection = false;
  let reflectionOpacity = 0;
  let reflectionIndex = 0;
  let hasHearingAid = false;

  // Add background music control
  let hasAttemptedMusicStart = false;

  // Add collision sound control
  let isColliding = false;

  // Function to load and play background music
  function loadAndPlayBackgroundMusic() {
    if (backgroundMusic) return;

    import('./music/simulator_background_music.MP3')
      .then(audioModule => {
        backgroundMusic = new Audio(audioModule.default);
        backgroundMusic.loop = true;
        backgroundMusic.volume = 0.3;
        
        // Add event listeners
        backgroundMusic.addEventListener('loadstart', () => {
          console.log('Background music: Loading started');
        });

        backgroundMusic.addEventListener('canplay', () => {
          console.log('Background music: Can play');
        });

        backgroundMusic.addEventListener('canplaythrough', () => {
          console.log('Background music: Can play through');
        });

        backgroundMusic.addEventListener('playing', () => {
          console.log('Background music: Now playing');
        });

        backgroundMusic.addEventListener('pause', () => {
          console.log('Background music: Paused');
          isMusicPlaying = false;
        });

        backgroundMusic.addEventListener('ended', () => {
          console.log('Background music: Ended');
          isMusicPlaying = false;
        });

        backgroundMusic.addEventListener('error', (error) => {
          console.error('Error loading background music:', error);
          console.error('Error code:', backgroundMusic.error?.code);
          console.error('Error message:', backgroundMusic.error?.message);
        });

        console.log('Background music source:', backgroundMusic.src);
      })
      .catch(error => {
        console.error('Failed to import audio file:', error);
      });
  }

  // Function to try starting music
  function tryStartMusic() {
    if (hasAttemptedMusicStart || !backgroundMusic || isMusicPlaying) return;
    
    hasAttemptedMusicStart = true;
    backgroundMusic.play()
      .then(() => {
        console.log('Background music: Started on user interaction');
        isMusicPlaying = true;
      })
      .catch(error => {
        console.error('Background music start failed:', error);
        hasAttemptedMusicStart = false; // Reset so we can try again
      });
  }

  // Function to resume background music
  function resumeBackgroundMusic() {
    if (backgroundMusic && !isMusicPlaying) {
      backgroundMusic.play()
        .then(() => {
          console.log('Background music: Resumed');
          isMusicPlaying = true;
        })
        .catch(error => {
          console.error('Background music resume failed:', error);
        });
    }
  }

  // Function to load collision sound
  function loadCollisionSound() {
    import('./music/Collision_Alert.mp3')
      .then(audioModule => {
        collisionSound = new Audio(audioModule.default);
        collisionSound.volume = 0.1; // Set volume to 5%
        collisionSound.loop = true; // Enable looping
      })
      .catch(error => {
        console.error('Failed to load collision sound:', error);
      });
  }

  // Function to load dialogue pop sound
  function loadDialoguePopSound() {
    import('./music/Dialogue_Pop.mp3')
      .then(audioModule => {
        dialoguePopSound = new Audio(audioModule.default);
        dialoguePopSound.volume = 0.1; // Set volume to 30%
      })
      .catch(error => {
        console.error('Failed to load dialogue pop sound:', error);
      });
  }

  // Function to play collision sound
  function playCollisionSound() {
    if (!collisionSound) return;
    
    if (!isColliding) {
      isColliding = true;
      collisionSound.currentTime = 0;
      collisionSound.play()
        .catch(error => {
          console.error('Failed to play collision sound:', error);
        });
    }
  }

  // Function to stop collision sound
  function stopCollisionSound() {
    if (!collisionSound) return;
    
    if (isColliding) {
      isColliding = false;
      collisionSound.pause();
      collisionSound.currentTime = 0;
    }
  }

  // Function to play dialogue pop sound
  function playDialoguePopSound() {
    if (!dialoguePopSound) return;
    
    dialoguePopSound.currentTime = 0;
    dialoguePopSound.play()
      .catch(error => {
        console.error('Failed to play dialogue pop sound:', error);
      });
  }

  // Function to load dream music
  function loadDreamMusic() {
    import('./music/Dream.mp3')
      .then(audioModule => {
        dreamMusic = new Audio(audioModule.default);
        dreamMusic.volume = 0.3;
      })
      .catch(error => {
        console.error('Failed to load dream music:', error);
      });
  }

  // Function to play dream music
  function playDreamMusic() {
    if (!dreamMusic) return;
    
    // Stop any existing playback first
    stopDreamMusic();
    
    // Add a small delay before playing to ensure cleanup is complete
    setTimeout(() => {
      dreamMusic.currentTime = 0;
      dreamMusic.play()
        .catch(error => {
          // Only log if it's not an abort error (which is expected during transitions)
          if (error.name !== 'AbortError') {
            console.error('Failed to play dream music:', error);
          }
        });
    }, 50);
  }

  // Function to stop dream music
  function stopDreamMusic() {
    if (!dreamMusic) return;
    
    try {
      // Pause and reset the audio
      dreamMusic.pause();
      dreamMusic.currentTime = 0;
    } catch (error) {
      console.error('Error stopping dream music:', error);
    }
  }

  // Function to load game completion music
  function loadGameCompleteMusic() {
    import('./music/Game_Complete.mp3')
      .then(audioModule => {
        gameCompleteMusic = new Audio(audioModule.default);
        gameCompleteMusic.volume = 0.3;
      })
      .catch(error => {
        console.error('Failed to load game completion music:', error);
      });
  }

  // Function to play game completion music
  function playGameCompleteMusic() {
    if (!gameCompleteMusic) return;
    
    try {
      gameCompleteMusic.currentTime = 0;
      gameCompleteMusic.play()
        .catch(error => {
          console.error('Failed to play game completion music:', error);
        });
    } catch (error) {
      console.error('Error playing game completion music:', error);
    }
  }

  // Function to stop game completion music
  function stopGameCompleteMusic() {
    if (!gameCompleteMusic) return;
    
    try {
      gameCompleteMusic.pause();
      gameCompleteMusic.currentTime = 0;
    } catch (error) {
      console.error('Error stopping game completion music:', error);
    }
  }

  // Function to load item acquisition sound
  function loadItemAcquisitionSound() {
    import('./music/Get_Item.mp3')
      .then(audioModule => {
        itemAcquisitionSound = new Audio(audioModule.default);
        itemAcquisitionSound.volume = 0.4;
      })
      .catch(error => {
        console.error('Failed to load item acquisition sound:', error);
      });
  }

  // Function to play item acquisition sound
  function playItemAcquisitionSound() {
    if (!itemAcquisitionSound) return;
    
    try {
      // Stop any existing playback
      itemAcquisitionSound.pause();
      itemAcquisitionSound.currentTime = 0;
      
      // Play the sound
      const playPromise = itemAcquisitionSound.play();
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          if (error.name !== 'AbortError') {
            console.error('Failed to play item acquisition sound:', error);
          }
        });
      }
    } catch (error) {
      console.error('Error playing item acquisition sound:', error);
    }
  }

  // Function to cleanup audio resources
  function cleanupAudio() {
    if (backgroundMusic) {
      try {
        backgroundMusic.pause();
        backgroundMusic.src = '';
        backgroundMusic = null;
        isMusicPlaying = false;
      } catch (error) {
        console.error('Error cleaning up background music:', error);
      }
    }
    if (collisionSound) {
      try {
        collisionSound.pause();
        collisionSound.src = '';
        collisionSound = null;
      } catch (error) {
        console.error('Error cleaning up collision sound:', error);
      }
    }
    if (dialoguePopSound) {
      try {
        dialoguePopSound.pause();
        dialoguePopSound.src = '';
        dialoguePopSound = null;
      } catch (error) {
        console.error('Error cleaning up dialogue pop sound:', error);
      }
    }
    if (dreamMusic) {
      try {
        dreamMusic.pause();
        dreamMusic.src = '';
        dreamMusic = null;
      } catch (error) {
        console.error('Error cleaning up dream music:', error);
      }
    }
    if (gameCompleteMusic) {
      try {
        gameCompleteMusic.pause();
        gameCompleteMusic.src = '';
        gameCompleteMusic = null;
      } catch (error) {
        console.error('Error cleaning up game completion music:', error);
      }
    }
    if (itemAcquisitionSound) {
      try {
        itemAcquisitionSound.pause();
        itemAcquisitionSound.src = '';
        itemAcquisitionSound = null;
      } catch (error) {
        console.error('Error cleaning up item acquisition sound:', error);
      }
    }
  }

  // Start loading music when game starts
  loadAndPlayBackgroundMusic();
  loadCollisionSound();
  loadDialoguePopSound();
  loadDreamMusic(); // Add dream music loading
  loadGameCompleteMusic(); // Add game completion music loading
  loadItemAcquisitionSound();  // Add this line

  // Function to start dream transition
  function startDreamTransition() {
    isTransitioningToDream = true;
    dreamTransitionProgress = 0;
    showDreamNarration = false;
    dreamNarrationOpacity = 0;
    stopBackgroundMusic(); // Stop background music when dream starts
    
    // Add a small delay before starting dream music
    setTimeout(() => {
      playDreamMusic();
    }, 100);
  }

  // Function to draw dream transition effect
  function drawDreamTransition() {
    if (!isTransitioningToDream) return;

    // Create spiral effect
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const maxRadius = Math.max(canvas.width, canvas.height) * 1.5;
    const spiralSteps = 200;
    const rotationSpeed = 0.2;
    const spiralWidth = 4;

    // Draw spiral
    ctx.save();
    ctx.beginPath();
    
    for (let line = 0; line < 3; line++) {
      ctx.beginPath();
      for (let i = 0; i < spiralSteps; i++) {
        const angle = i * rotationSpeed + (line * Math.PI / 3);
        const radius = (maxRadius * dreamTransitionProgress) * (i / spiralSteps);
        const x = centerX + radius * Math.cos(angle);
        const y = centerY + radius * Math.sin(angle);
        
        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }
      
      ctx.strokeStyle = `rgba(0, 0, 0, ${dreamTransitionProgress * 0.8})`;
      ctx.lineWidth = spiralWidth;
      ctx.stroke();
    }
    ctx.restore();

    // Apply grayscale filter with fade
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    const grayscaleProgress = Math.min(dreamTransitionProgress * 1.5, 1);
    
    for (let i = 0; i < data.length; i += 4) {
      const avg = (data[i] + data[i + 1] + data[i + 2]) / 3;
      data[i] = data[i] * (1 - grayscaleProgress) + avg * grayscaleProgress;
      data[i + 1] = data[i + 1] * (1 - grayscaleProgress) + avg * grayscaleProgress;
      data[i + 2] = data[i + 2] * (1 - grayscaleProgress) + avg * grayscaleProgress;
    }
    ctx.putImageData(imageData, 0, 0);

    // Update transition progress - increased speed for 1 second duration
    if (dreamTransitionProgress < 1) {
      dreamTransitionProgress += 0.02;
    } else {
      isTransitioningToDream = false;
      showDreamNarration = true;
      stopDreamMusic(); // Stop dream music when transition is complete
    }
  }

  // Function to draw dream narration
  function drawDreamNarration() {
    if (!showDreamNarration) return;

    // No fade-in: show instantly
    ctx.fillStyle = 'rgba(0, 0, 0, 1)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = 'white';
    ctx.font = 'bold 28px Arial';
    ctx.textAlign = 'center';
    const line = dreamNarrationText[dreamNarrationIndex];
    ctx.fillText(line, canvas.width / 2, canvas.height / 2);
    ctx.font = 'italic 18px Arial';
    ctx.fillText("Press ENTER to continue", canvas.width / 2, canvas.height / 2 + 50);
  }

  // Function to draw poster
  function drawPoster() {
    if (!showPoster) {
      return;
    }

    if (posterOpacity < 1) {
      posterOpacity += 0.02;
    }

    const boxWidth = canvas.width * 0.8;
    const boxHeight = canvas.height * 0.8;
    const boxX = (canvas.width - boxWidth) / 2;
    const boxY = (canvas.height - boxHeight) / 2;
    const padding = 40;
    const lineHeight = 30;
    const maxTextWidth = boxWidth - (padding * 2);

    // Draw poster background
    ctx.fillStyle = `rgba(255, 255, 255, ${posterOpacity})`;
    ctx.strokeStyle = `rgba(0, 0, 0, ${posterOpacity})`;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.roundRect(boxX, boxY, boxWidth, boxHeight, 10);
    ctx.fill();
    ctx.stroke();

    // Draw poster title (centered)
    ctx.fillStyle = `rgba(0, 0, 0, ${posterOpacity})`;
    ctx.font = 'bold 32px Arial';
    ctx.textAlign = 'center';
    const titleY = boxY + padding + 40;
    ctx.fillText(DREAM_POSTER.title, boxX + boxWidth / 2, titleY);

    // Set up for left-aligned content
    ctx.textAlign = 'left';
    ctx.font = 'bold 22px Arial';
    let currentY = titleY + padding + 10;
    const maxY = boxY + boxHeight - padding - 40;
    const leftX = boxX + padding;

    // Helper to draw wrapped text (improved to avoid breaking phrases)
    function drawWrappedText(text, y, fontStyle = '18px Arial', fontWeight = 'normal') {
      ctx.font = fontStyle;
      const words = text.split(' ');
      let line = '';
      for (let i = 0; i < words.length; i++) {
        const testLine = line + (line ? ' ' : '') + words[i];
        const metrics = ctx.measureText(testLine);
        if (metrics.width > maxTextWidth && line !== '') {
          ctx.fillText(line, leftX, y);
          y += lineHeight;
          line = words[i];
        } else {
          line = testLine;
        }
      }
      if (line) {
        ctx.fillText(line, leftX, y);
        y += lineHeight;
      }
      return y;
    }

    // Display only the summary statistics from the API
    if (auslanDhhChildrenData && Array.isArray(auslanDhhChildrenData)) {
      const summaryData = auslanDhhChildrenData.find(item => item.id === 13);
      if (summaryData) {
        currentY = drawWrappedText('Did you know?', currentY, 'bold 20px Arial');
        currentY += lineHeight / 2;
        const statsText = `In Australia, around ${summaryData.percentage}% of students speak AUSLAN at home based on statistics from ${summaryData.year}.`;
        currentY = drawWrappedText(statsText, currentY, '18px Arial');
        currentY += lineHeight * 1.2;
      }
    }

    // Draw static poster content (left-aligned)
    ctx.font = '18px Arial';
    DREAM_POSTER.content.forEach((paragraph) => {
      if (paragraph.trim() === '') {
        currentY += lineHeight / 2;
        return;
      }
      currentY = drawWrappedText(paragraph, currentY, '18px Arial');
      currentY += lineHeight / 2;
    });

    // Draw continue prompt at the bottom (keep this centered)
    ctx.font = 'italic 18px Arial';
    ctx.textAlign = 'right';
    ctx.fillText("Press SPACE to continue", boxX + boxWidth - padding, boxY + boxHeight - padding);
  }

  // Function to draw reflection
  function drawReflection() {
    if (!showReflection) return;

    if (reflectionOpacity < 1) {
      reflectionOpacity += 0.02;
    }

    const boxWidth = canvas.width * 0.8;
    const boxHeight = 150;
    const boxX = (canvas.width - boxWidth) / 2;
    const boxY = canvas.height - boxHeight - 20;

    // Draw reflection box
    ctx.fillStyle = `rgba(255, 255, 255, ${reflectionOpacity})`;
    ctx.strokeStyle = `rgba(0, 0, 0, ${reflectionOpacity})`;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect(boxX, boxY, boxWidth, boxHeight, 10);
    ctx.fill();
    ctx.stroke();

    // Draw reflection text
    ctx.fillStyle = `rgba(0, 0, 0, ${reflectionOpacity})`;
    ctx.font = 'italic 18px Arial';
    ctx.textAlign = 'center';
    DREAM_REFLECTION.forEach((line, index) => {
      const y = boxY + 60 + (index * 40);
      ctx.fillText(line, canvas.width / 2, y);
    });
  }

  // Function to start dialogue with an NPC
  function startDialogue(npcId) {
    if (isInDialogue) {
      return;
    }
    
    currentNPC = npcId;
    isInDialogue = true;
    playerCanMove = false;
    
    // Play dialogue pop sound when starting dialogue
    playDialoguePopSound();
    
    if (npcId === NPC_IDS.OLD_MAN) {
      if (!hasKnowledgeBadge) {
        currentDialogue = NPC_DIALOGUES[npcId].initial;
      } else {
        currentDialogue = [{ speaker: "Old Man", text: "Visit www.signmates.me to learn more about DHH individuals and sign languages!" }];
      }
    } else if (isNPCInteractable(npcId, currentGameState)) {
      if (currentGameState === GAME_STATES.DREAM_START || 
          currentGameState === GAME_STATES.DREAM_POSTER || 
          currentGameState === GAME_STATES.DREAM_NURSE || 
          currentGameState === GAME_STATES.DREAM_DOCTOR) {
        currentDialogue = DREAM_DIALOGUES[npcId]?.initial || [];
      } else if (npcId === NPC_IDS.TEACHER2) {
        // Special case for Teacher 2 - use beforeBook dialogue
        currentDialogue = NPC_DIALOGUES[npcId]?.beforeBook || [];
      } else {
        currentDialogue = NPC_DIALOGUES[npcId]?.initial || [];
      }
    } else {
      currentDialogue = getDefaultDialogue(npcId, currentGameState, hasKnowledgeBadge);
    }
    
    currentDialogueIndex = 0;
  }

  // Add state for question handling
  let selectedOption = null;
  let showAnswerFeedback = false;
  let answerFeedback = '';
  let isWaitingForAnswer = false;

  // Function to handle option selection
  function handleOptionSelection(optionNumber) {
    if (!isWaitingForAnswer) return;
    
    const currentDialogueItem = currentDialogue[currentDialogueIndex];
    if (!currentDialogueItem.isQuestion) return;

    selectedOption = optionNumber;
    isWaitingForAnswer = false;
    
    // Check if answer is correct
    if (optionNumber === currentDialogueItem.correctAnswer) {
      answerFeedback = "Correct!";
    } else {
      answerFeedback = "Incorrect!";
    }
    showAnswerFeedback = true;
  }

  // Add achievement state
  let showAchievement = false;
  let achievementText = '';
  let achievementTimer = null;
  let achievementOpacity = 0;
  let achievementY = -100; // Start above the screen

  // Function to show achievement
  function showAchievementUnlocked(text) {
    achievementText = text;
    showAchievement = true;
    achievementOpacity = 0;
    achievementY = -100;
    
    // Play item acquisition sound
    playItemAcquisitionSound();
    
    // Clear any existing timer
    if (achievementTimer) {
      clearTimeout(achievementTimer);
    }
    
    // Set timer to hide achievement
    achievementTimer = setTimeout(() => {
      showAchievement = false;
    }, 3000);
  }

  // Function to draw achievement
  function drawAchievement() {
    if (!showAchievement) return;

    // Animate achievement
    if (achievementOpacity < 1) {
      achievementOpacity += 0.02;
    }
    if (achievementY < 50) {
      achievementY += 2;
    }

    // Draw achievement box
    const boxWidth = 300;
    const boxHeight = 80;
    const boxX = (canvas.width - boxWidth) / 2;
    const boxY = achievementY;

    // Draw background with blur effect
    ctx.fillStyle = `rgba(255, 255, 255, ${achievementOpacity * 0.95})`;
    ctx.strokeStyle = `rgba(0, 0, 0, ${achievementOpacity * 0.5})`;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect(boxX, boxY, boxWidth, boxHeight, 10);
    ctx.fill();
    ctx.stroke();

    // Draw achievement icon (book emoji or trophy emoji)
    ctx.font = '24px Arial';
    ctx.textAlign = 'center';
    ctx.fillStyle = `rgba(0, 0, 0, ${achievementOpacity})`;
    // Choose emoji based on achievement text
    let achievementEmoji;
    if (achievementText.includes('Knowledge Badge')) {
      achievementEmoji = '🏆';
    } else if (achievementText.includes('Hearing Aid')) {
      achievementEmoji = '👂';
    } else {
      achievementEmoji = '📚';
    }
    ctx.fillText(achievementEmoji, boxX + 30, boxY + 45);

    // Draw achievement text
    ctx.font = 'bold 16px Arial';
    ctx.textAlign = 'left';
    ctx.fillStyle = `rgba(0, 0, 0, ${achievementOpacity})`;
    ctx.fillText('Achievement Unlocked!', boxX + 60, boxY + 30);
    ctx.font = '14px Arial';
    ctx.fillText(achievementText, boxX + 60, boxY + 55);
  }

  // Add a function to handle game state changes
  function handleGameStateChange(newState) {
    if (newState === GAME_STATES.DREAM_DOCTOR) {
      startDreamExitTransition();
    }
  }

  // Modify the advanceDialogue function
  function advanceDialogue() {
    if (!isInDialogue || !currentDialogue) return;
    
    // If showing answer feedback, clear it and move to next dialogue
    if (showAnswerFeedback) {
      showAnswerFeedback = false;
      answerFeedback = '';
      selectedOption = null;
      currentDialogueIndex++;
    } else {
      const currentDialogueItem = currentDialogue[currentDialogueIndex];
      
      // Check if this is the badge reward message
      if (currentNPC === NPC_IDS.OLD_MAN && 
          currentDialogueItem.speaker === "Old Man" && 
          currentDialogueItem.text === "I am giving you the Knowledge Badge as a reward for your learning.") {
        hasKnowledgeBadge = true;
        showAchievementUnlocked("Received Knowledge Badge");
      }
      
      // Check if we have a valid dialogue item
      if (!currentDialogueItem) {
        // End dialogue if we've reached the end
        isInDialogue = false;
        playerCanMove = true;
        currentDialogue = null;
        currentDialogueIndex = 0;
        isWaitingForAnswer = false;
        selectedOption = null;
        showAnswerFeedback = false;
        answerFeedback = '';
        
        // Update game state if this was an interactable NPC
        if (currentNPC && isNPCInteractable(currentNPC, currentGameState)) {
          const nextState = getNextGameState(currentNPC, currentGameState);
          
          // Handle dream state transitions
          if (currentGameState === GAME_STATES.MET_CHILD1 && nextState === GAME_STATES.DREAM_START) {
            startDreamTransition();
          } else if (currentGameState === GAME_STATES.DREAM_START && nextState === GAME_STATES.DREAM_POSTER) {
            showPoster = true;
            posterOpacity = 0; // Reset opacity to ensure fade in
          } else if (currentGameState === GAME_STATES.DREAM_NURSE && nextState === GAME_STATES.DREAM_DOCTOR) {
            hasHearingAid = true;
            showAchievementUnlocked("Received Hearing Aid");
            resumeBackgroundMusic(); // Resume music when hearing aid is received
          }
          
          // Update state and trigger state change handler
          currentGameState = nextState;
          handleGameStateChange(currentGameState);
        }
        
        currentNPC = null;
        return;
      }

      // Check if this is the AUSLAN book dialogue
      if (currentNPC === NPC_IDS.TEACHER2 && 
          currentDialogueItem.speaker === "System" && 
          currentDialogueItem.text === "You received an AUSLAN book!") {
        showAchievementUnlocked("Received AUSLAN Sign Book");
      }
      
      // Check if this is the hearing aid dialogue
      if (currentNPC === NPC_IDS.NURSE && 
          currentDialogueItem.speaker === "System" && 
          currentDialogueItem.text === "You received a hearing aid!") {
        showAchievementUnlocked("Received Hearing Aid");
        resumeBackgroundMusic(); // Resume music when hearing aid is received
      }
      
      // If current dialogue is a question, wait for answer
      if (currentDialogueItem.isQuestion === true) {
        isWaitingForAnswer = true;
        return;
      }
      
      currentDialogueIndex++;
    }
    
    // Check if we've reached the end of the dialogue
    if (currentDialogueIndex >= currentDialogue.length) {
      // End of dialogue
      isInDialogue = false;
      playerCanMove = true;
      currentDialogue = null;
      currentDialogueIndex = 0;
      isWaitingForAnswer = false;
      selectedOption = null;
      showAnswerFeedback = false;
      answerFeedback = '';
      
      // Update game state if this was an interactable NPC
      if (currentNPC && isNPCInteractable(currentNPC, currentGameState)) {
        const nextState = getNextGameState(currentNPC, currentGameState);
        
        // Handle dream state transitions
        if (currentGameState === GAME_STATES.MET_CHILD1 && nextState === GAME_STATES.DREAM_START) {
          startDreamTransition();
        } else if (currentGameState === GAME_STATES.DREAM_START && nextState === GAME_STATES.DREAM_POSTER) {
          showPoster = true;
          posterOpacity = 0; // Reset opacity to ensure fade in
        } else if (currentGameState === GAME_STATES.DREAM_NURSE && nextState === GAME_STATES.DREAM_DOCTOR) {
          hasHearingAid = true;
        }
        
        // Update state and trigger state change handler
        currentGameState = nextState;
        handleGameStateChange(currentGameState);
      }
      
      currentNPC = null;
    }
  }

  // Function to wrap text to fit within a width
  function wrapText(ctx, text, maxWidth) {
    const words = text.split(' ');
    const lines = [];
    let currentLine = words[0];

    for (let i = 1; i < words.length; i++) {
      const word = words[i];
      const width = ctx.measureText(currentLine + " " + word).width;
      if (width < maxWidth && currentLine !== '') {
        currentLine += " " + word;
      } else {
        lines.push(currentLine);
        currentLine = word;
      }
    }
    lines.push(currentLine);
    return lines;
  }

  // Function to draw dialogue box
  function drawDialogueBox(dialogue) {
    if (!dialogue) return;

    // Increase box width from 80% to 90% of canvas width
    const boxWidth = canvas.width * 0.9;
    // Reduce box height for questions to 270px for a more compact look
    const boxHeight = dialogue.isQuestion ? 270 : 160;
    const boxX = (canvas.width - boxWidth) / 2;
    const boxY = canvas.height - boxHeight - 20;
    const padding = 15;
    const lineHeight = 24;
    const maxTextWidth = boxWidth - (padding * 2);

    // Draw box background with white background and dark border
    ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
    ctx.strokeStyle = '#2c3e50';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.roundRect(boxX, boxY, boxWidth, boxHeight, 5);
    ctx.fill();
    ctx.stroke();

    // Draw speaker name with retro style
    ctx.fillStyle = '#2c3e50';
    ctx.font = 'bold 19px "Press Start 2P", monospace';
    ctx.textAlign = 'left';
    ctx.fillText(dialogue.speaker.toUpperCase(), boxX + padding, boxY + 35);

    // Draw dialogue text with wrapping
    ctx.font = '17px "Press Start 2P", monospace';
    const text = dialogue.text;
    
    // Split text into paragraphs (handle \n)
    const paragraphs = text.split('\n');
    let y = boxY + 65;

    // Process each paragraph
    for (const paragraph of paragraphs) {
      // Skip options if showing answer feedback
      if (showAnswerFeedback && paragraph.startsWith('Options:')) continue;
      
      // Wrap text for each paragraph
      const lines = wrapText(ctx, paragraph, maxTextWidth);
      
      // Draw each line
      for (const line of lines) {
        // Check if we have enough space for another line
        if (y + lineHeight > boxY + boxHeight - padding) {
          ctx.fillStyle = '#7f8c8d';
          ctx.fillText('...', boxX + padding, y);
          break;
        }
        ctx.fillStyle = '#2c3e50';
        ctx.fillText(line, boxX + padding, y);
        y += lineHeight;
      }
      
      // Add a small gap between paragraphs
      y += lineHeight / 2;
    }

    // Draw options if it's a question and not showing feedback
    if (dialogue.isQuestion && !showAnswerFeedback) {
      const options = dialogue.text.split('\n').filter(line => line.startsWith('Options:'));
      if (options.length > 0) {
        const optionLines = options[0].split('\n').slice(1);
        y += lineHeight / 3; // Slightly reduced spacing before options
        // Calculate maximum option width to ensure proper alignment
        const maxOptionWidth = Math.max(...optionLines.map(option => 
          ctx.measureText(option).width
        ));
        optionLines.forEach((option, index) => {
          const optionNumber = index + 1;
          const isSelected = selectedOption === optionNumber;
          // Draw option background if selected
          if (isSelected) {
            ctx.fillStyle = 'rgba(44, 62, 80, 0.1)';
            ctx.fillRect(boxX + padding - 5, y - 20, maxTextWidth + 10, lineHeight);
          }
          // Draw option number and text with proper spacing
          ctx.fillStyle = isSelected ? '#e74c3c' : '#2c3e50';
          const optionText = `${optionNumber}. ${option}`;
          ctx.fillText(optionText, boxX + padding, y);
          y += lineHeight - 2; // Reduce gap between options for compactness
        });
      }
    }

    // Draw answer feedback if showing
    if (showAnswerFeedback) {
      ctx.fillStyle = answerFeedback.includes('Correct') ? '#27ae60' : '#e74c3c';
      ctx.font = 'bold 19px "Press Start 2P", monospace';
      ctx.fillText(answerFeedback.toUpperCase(), boxX + padding, y + lineHeight / 2);
    }

    // Draw continue prompt with retro style
    ctx.font = '15px "Press Start 2P", monospace';
    ctx.fillStyle = '#7f8c8d';
    ctx.textAlign = 'right';
    const continueText = isWaitingForAnswer ? 'ENTER OPTION (1-4)' : 'PRESS SPACE TO CONTINUE';
    ctx.fillText(continueText, boxX + boxWidth - padding, boxY + boxHeight - padding);
  }

  // Function to update interaction zones
  function updateInteractionZones() {
    for (const movable of movables) {
      if (movable.npcId && movable.update) {
        movable.update();
      }
    }
  }

  // Add dream exit state variables
  let isExitingDream = false;
  let dreamExitProgress = 0;
  let showWakeUpMessage = false;
  let wakeUpOpacity = 0;
  let wakeUpIndex = 0;
  const wakeUpText = ["You have woken up from the dream..."];
  let showFinalReflection = false;
  let finalReflectionOpacity = 0;
  let isGameEnding = false;
  let gameEndOpacity = 0;

  // Function to start dream exit transition
  function startDreamExitTransition() {
    isExitingDream = true;
    dreamExitProgress = 0;
    showWakeUpMessage = false;
    wakeUpOpacity = 0;
    showFinalReflection = false;
    finalReflectionOpacity = 0;
    isGameEnding = false;
    gameEndOpacity = 0;
    playerCanMove = false; // Prevent player movement during transition
    
    // Add a small delay before starting dream music
    setTimeout(() => {
      playDreamMusic();
    }, 100);

    // Store the original starting position
    const startX = canvas.width / 2 - 192 / 4 / 2;
    const startY = canvas.height / 2 - 68 / 2;

    // Reset player position immediately
    player.position.x = startX;
    player.position.y = startY;

    // Reset camera position
    movables.forEach(m => {
      if (m !== player) {
        m.position.x = offset.x;
        m.position.y = offset.y;
      }
    });

    // Force a redraw of the game elements
    animate();
  }

  // Function to draw dream exit transition
  function drawDreamExitTransition() {
    if (!isExitingDream) return;

    // Create reverse spiral effect
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const maxRadius = Math.max(canvas.width, canvas.height) * 1.5;
    const spiralSteps = 200;
    const rotationSpeed = -0.2;
    const spiralWidth = 4;

    // Draw spiral
    ctx.save();
    ctx.beginPath();
    
    for (let line = 0; line < 3; line++) {
      ctx.beginPath();
      for (let i = 0; i < spiralSteps; i++) {
        const angle = i * rotationSpeed + (line * Math.PI / 3);
        const radius = (maxRadius * (1 - dreamExitProgress)) * (i / spiralSteps);
        const x = centerX + radius * Math.cos(angle);
        const y = centerY + radius * Math.sin(angle);
        
        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }
      
      ctx.strokeStyle = `rgba(0, 0, 0, ${(1 - dreamExitProgress) * 0.8})`;
      ctx.lineWidth = spiralWidth;
      ctx.stroke();
    }
    ctx.restore();

    // Remove grayscale filter
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    const grayscaleProgress = Math.max(1 - dreamExitProgress * 1.5, 0);
    
    for (let i = 0; i < data.length; i += 4) {
      const avg = (data[i] + data[i + 1] + data[i + 2]) / 3;
      data[i] = data[i] * (1 - grayscaleProgress) + avg * grayscaleProgress;
      data[i + 1] = data[i + 1] * (1 - grayscaleProgress) + avg * grayscaleProgress;
      data[i + 2] = data[i + 2] * (1 - grayscaleProgress) + avg * grayscaleProgress;
    }
    ctx.putImageData(imageData, 0, 0);

    // Update transition progress - increased speed for 1 second duration
    if (dreamExitProgress < 1) {
      dreamExitProgress += 0.02;
    } else {
      isExitingDream = false;
      showWakeUpMessage = true;
      stopDreamMusic(); // Stop dream music when exit transition is complete
      // Ensure player is still at starting position
      player.position.x = canvas.width / 2 - 192 / 4 / 2;
      player.position.y = canvas.height / 2 - 68 / 2;
    }
  }

  // Function to draw wake up message
  function drawWakeUpMessage() {
    if (!showWakeUpMessage) return;

    // No fade-in: show instantly
    ctx.fillStyle = 'rgba(0, 0, 0, 1)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = 'white';
    ctx.font = 'bold 32px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(wakeUpText[wakeUpIndex], canvas.width / 2, canvas.height / 2);
    ctx.font = 'italic 18px Arial';
    ctx.fillText("Press ENTER to continue", canvas.width / 2, canvas.height / 2 + 50);
  }

  // Function to draw final reflection
  function drawFinalReflection() {
    if (!showFinalReflection) return;

    // Draw semi-transparent black background
    ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    // Draw reflection box
    const boxWidth = canvas.width * 0.8;
    const boxHeight = 200;
    const boxX = (canvas.width - boxWidth) / 2;
    const boxY = canvas.height - boxHeight - 20;
    const padding = 20;
    const lineHeight = 30;
    ctx.fillStyle = 'white';
    ctx.strokeStyle = 'black';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect(boxX, boxY, boxWidth, boxHeight, 10);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = 'black';
    ctx.font = 'italic 20px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(DREAM_REFLECTION[reflectionIndex], canvas.width / 2, boxY + 110);
    ctx.font = 'italic 18px Arial';
    ctx.fillText("Press ENTER to continue", canvas.width / 2, boxY + boxHeight - 30);
  }

  // Function to draw game end
  function drawGameEnd() {
    if (!isGameEnding) return;

    if (gameEndOpacity < 1) {
      gameEndOpacity += 0.02;
      // Stop background music and play completion music when game end screen starts appearing
      if (gameEndOpacity >= 0.1) {  // Stop music early in the fade in
        stopBackgroundMusic();
        stopDreamMusic();
        // Play completion music when the text becomes visible
        if (gameEndOpacity >= 0.5) {
          playGameCompleteMusic();
        }
      }
    }

    // Draw semi-transparent black background
    ctx.fillStyle = `rgba(0, 0, 0, ${gameEndOpacity * 0.9})`;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw game end message
    ctx.fillStyle = `rgba(255, 255, 255, ${gameEndOpacity})`;
    ctx.font = 'bold 48px Arial';
    ctx.textAlign = 'center';
    ctx.fillText("GAME END", canvas.width / 2, canvas.height / 2);
  }

  // Add function to draw start screen
  function drawStartScreen() {
    if (!showStartScreen) return;

    // Draw start image
    ctx.drawImage(startImage, 0, 0, canvas.width, canvas.height);

    // Draw blinking text
    if (showBlinkText) {
      ctx.fillStyle = 'white';
      ctx.font = 'bold 32px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('Press ENTER to continue', canvas.width / 2, canvas.height - 50);
    }

    // Update blink timer
    startScreenBlinkTimer += 1;
    if (startScreenBlinkTimer >= 30) { // Blink every 30 frames (half a second at 60fps)
      showBlinkText = !showBlinkText;
      startScreenBlinkTimer = 0;
    }
  }

  // Add function to draw introduction
  function drawIntroduction() {
    if (!showIntroduction) return;

    // Draw semi-transparent black background
    ctx.fillStyle = 'rgba(0, 0, 0, 0.9)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw text box
    const boxWidth = canvas.width * 0.8;
    const boxHeight = canvas.height * 0.8;
    const boxX = (canvas.width - boxWidth) / 2;
    const boxY = (canvas.height - boxHeight) / 2;
    const padding = 40;
    const lineHeight = 30;

    // Draw box background
    ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
    ctx.strokeStyle = '#2c3e50';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.roundRect(boxX, boxY, boxWidth, boxHeight, 10);
    ctx.fill();
    ctx.stroke();

    // Draw text
    ctx.fillStyle = '#2c3e50';
    ctx.font = '20px Arial';
    ctx.textAlign = 'left';
    let y = boxY + padding;

    // Draw each line of text
    introductionText.forEach((line, index) => {
      // Special styling for the first line (title)
      if (index === 0) {
        ctx.font = 'bold 28px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(line, canvas.width / 2, y);
        ctx.font = '20px Arial';
        ctx.textAlign = 'left';
        y += lineHeight * 1.5;
        return;
      }

      // Handle empty lines
      if (line === "") {
        y += lineHeight / 2;
        return;
      }

      // Wrap text for long lines
      const words = line.split(' ');
      let currentLine = words[0];
      for (let i = 1; i < words.length; i++) {
        const word = words[i];
        const width = ctx.measureText(currentLine + " " + word).width;
        if (width < boxWidth - (padding * 2)) {
          currentLine += " " + word;
        } else {
          ctx.fillText(currentLine, boxX + padding, y);
          y += lineHeight;
          currentLine = word;
        }
      }
      ctx.fillText(currentLine, boxX + padding, y);
      y += lineHeight;
    });

    // Draw continue prompt
    ctx.font = 'italic 18px Arial';
    ctx.textAlign = 'center';
    ctx.fillStyle = '#7f8c8d';
    ctx.fillText("Press ENTER to continue", canvas.width / 2, boxY + boxHeight - padding);
  }

  // Modify the animate function
  function animate() {
    window.requestAnimationFrame(animate);
    
    // Clear the canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Draw start screen if active
    if (showStartScreen) {
      drawStartScreen();
      return;
    }

    // Draw introduction if active
    if (showIntroduction) {
      drawIntroduction();
      return;
    }
    
    // Draw game elements
    background.draw(ctx);
    boundaries.forEach(boundary => boundary.draw(ctx));
    npcSprites.forEach(npc => npc.draw(ctx));
    
    // Only draw player if it exists
    if (player && player.position) {
      player.draw(ctx);
    }
    
    foreground.draw(ctx);

    // Draw dream effects
    drawDreamTransition();
    drawDreamNarration();
    drawDreamExitTransition();
    drawWakeUpMessage();
    drawPoster();
    drawReflection();
    drawFinalReflection();
    drawGameEnd();

    // Draw dialogue box if in dialogue
    if (isInDialogue && currentDialogue && currentDialogue[currentDialogueIndex]) {
      drawDialogueBox(currentDialogue[currentDialogueIndex]);
    }

    // Draw achievement if active
    drawAchievement();

    // Handle player movement only if not in dialogue and player exists
    if (playerCanMove && player && player.position) {
      let moving = true;
      player.moving = false;
      let isCollidingThisFrame = false;

      if ((keys.w.pressed && lastKey === 'w') || (keys.ArrowUp.pressed && lastKey === 'ArrowUp')) {
        player.moving = true;
        player.image = player.sprites.up;
        for (const boundary of boundaries) {
          if (rectangleCollision({ rectangle1: player, rectangle2: { ...boundary, position: { x: boundary.position.x, y: boundary.position.y + 3 } } })) {
            moving = false;
            isCollidingThisFrame = true;
            break;
          }
        }
        if (moving) {
          movables.forEach(m => {
            m.position.y += 3;
            if (m.update) m.update();
          });
        }
      } else if ((keys.s.pressed && lastKey === 's') || (keys.ArrowDown.pressed && lastKey === 'ArrowDown')) {
        player.moving = true;
        player.image = player.sprites.down;
        for (const boundary of boundaries) {
          if (rectangleCollision({ rectangle1: player, rectangle2: { ...boundary, position: { x: boundary.position.x, y: boundary.position.y - 3 } } })) {
            moving = false;
            isCollidingThisFrame = true;
            break;
          }
        }
        if (moving) {
          movables.forEach(m => {
            m.position.y -= 3;
            if (m.update) m.update();
          });
        }
      } else if ((keys.a.pressed && lastKey === 'a') || (keys.ArrowLeft.pressed && lastKey === 'ArrowLeft')) {
        player.moving = true;
        player.image = player.sprites.left;
        for (const boundary of boundaries) {
          if (rectangleCollision({ rectangle1: player, rectangle2: { ...boundary, position: { x: boundary.position.x + 3, y: boundary.position.y } } })) {
            moving = false;
            isCollidingThisFrame = true;
            break;
          }
        }
        if (moving) {
          movables.forEach(m => {
            m.position.x += 3;
            if (m.update) m.update();
          });
        }
      } else if ((keys.d.pressed && lastKey === 'd') || (keys.ArrowRight.pressed && lastKey === 'ArrowRight')) {
        player.moving = true;
        player.image = player.sprites.right;
        for (const boundary of boundaries) {
          if (rectangleCollision({ rectangle1: player, rectangle2: { ...boundary, position: { x: boundary.position.x - 3, y: boundary.position.y } } })) {
            moving = false;
            isCollidingThisFrame = true;
            break;
          }
        }
        if (moving) {
          movables.forEach(m => {
            m.position.x -= 3;
            if (m.update) m.update();
          });
        }
      }

      // Handle collision sound based on collision state
      if (isCollidingThisFrame) {
        playCollisionSound();
      } else {
        stopCollisionSound();
      }
    }

    // Draw interaction prompt only if no narrative overlays are active
    if (!isInDialogue && playerCanMove && player && player.position && !showDreamNarration && !showWakeUpMessage && !showFinalReflection && !showPoster) {
      const nearbyNPC = isPlayerNearNPC();
      if (nearbyNPC) {
        // Draw interaction prompt
        const boxWidth = 200;
        const boxHeight = 40;
        const boxX = canvas.width / 2 - boxWidth / 2;
        const boxY = canvas.height - 100;
        ctx.fillStyle = 'rgba(0, 0, 0, 0.2)';
        ctx.fillRect(boxX, boxY, boxWidth, boxHeight);
        ctx.fillStyle = 'white';
        ctx.font = '14px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('Press SPACE to interact', canvas.width / 2, boxY + boxHeight / 2);
      }
    }
  }

  // Modify the keydown event listener
  window.addEventListener('keydown', e => {
    // Block all key presses during dream transition
    if (isTransitioningToDream) {
      e.preventDefault();
      return;
    }
    // Handle start screen and introduction
    if (showStartScreen) {
      if (e.code === 'Enter') {
        e.preventDefault();
        showStartScreen = false;
        showIntroduction = true;
        return;
      }
      return;
    }

    if (showIntroduction) {
      if (e.code === 'Enter') {
        e.preventDefault();
        showIntroduction = false;
        // Start loading music and other resources when game actually starts
        loadAndPlayBackgroundMusic();
        loadCollisionSound();
        loadDialoguePopSound();
        loadDreamMusic();
        loadGameCompleteMusic();
        loadItemAcquisitionSound();
        return;
      }
      return;
    }

    // Try to start music on first key press (only after introduction)
    tryStartMusic();

    // If a narrative overlay is active, only allow Enter key
    if (showDreamNarration || showWakeUpMessage || showFinalReflection) {
      if (e.code !== 'Enter') {
        e.preventDefault();
        return;
      }
    }

    // Use Enter key for narrative screens
    if (e.code === 'Enter') {
      e.preventDefault();
      // Dream narration advance
      if (showDreamNarration) {
        if (dreamNarrationIndex < dreamNarrationText.length - 1) {
          dreamNarrationIndex++;
        } else {
          showDreamNarration = false;
          dreamNarrationIndex = 0;
        }
        return;
      }
      // Wake up message advance
      if (showWakeUpMessage) {
        if (wakeUpIndex < wakeUpText.length - 1) {
          wakeUpIndex++;
        } else {
          showWakeUpMessage = false;
          wakeUpIndex = 0;
          showFinalReflection = true;
        }
        return;
      }
      // Dream reflection advance
      if (showFinalReflection) {
        if (reflectionIndex < DREAM_REFLECTION.length - 1) {
          reflectionIndex++;
        } else {
          showFinalReflection = false;
          reflectionIndex = 0;
          isGameEnding = true;
        }
        return;
      }
    }
    // Spacebar for other actions
    if (e.code === 'Space') {
      e.preventDefault();
      if (showPoster) {
        showPoster = false;
        posterOpacity = 0;
        return;
      }
      if (showReflection) {
        showReflection = false;
        reflectionOpacity = 0;
        return;
      }
      if (isInDialogue) {
        if (!isWaitingForAnswer) {
          advanceDialogue();
        }
      } else if (playerCanMove) {
        const nearbyNPC = isPlayerNearNPC();
        if (nearbyNPC) {
          startDialogue(nearbyNPC);
        }
      }
    }
    
    // Handle option selection with number keys
    if (isWaitingForAnswer && ['Digit1', 'Digit2', 'Digit3', 'Digit4'].includes(e.code)) {
      const optionNumber = parseInt(e.code.replace('Digit', ''));
      handleOptionSelection(optionNumber);
    }
    
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault(); // Prevent scrolling
    }

    if (e.key in keys) {
      keys[e.key].pressed = true;
      lastKey = e.key;
    }
  });

  window.addEventListener('keyup', e => {
    if (e.key in keys) keys[e.key].pressed = false;
  });

  animate();

  // Add cleanup to window unload
  window.addEventListener('unload', cleanupAudio);

  // Fetch API data in parallel at game start
  Promise.all([
    fetch(`${API_URL}/api/auslan-dhh-children`)
      .then(res => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then(data => {
        console.log('AUSLAN DHH Children API result:', data);
        auslanDhhChildrenData = data;
        return data;
      })
      .catch(error => {
        console.error('Error fetching AUSLAN DHH Children data:', error);
        return null;
      }),
    // Remove the second API call since we only need the AUSLAN DHH Children data
    Promise.resolve(null) // Placeholder to maintain Promise.all structure
  ]).then(([auslanData]) => {
    auslanDhhChildrenData = auslanData;
    // Remove emotionEmpathyComparisonData assignment since we're not using it
  });
}
